/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { ProductivityDraft } from 'containers/new-route-manager/route-productivity/types'

/**
 * Office edits keyed by `${routeId}|${serviceDate}`, overlaid on whatever the
 * route-day endpoint returned for that day. Deliberately outside the
 * redux-persist whitelist: an edit that outlived the session would be a saved
 * value the server never received.
 */
export const ROUTE_PRODUCTIVITY_DRAFT_KEY = 'routeProductivityDraft' as const

export const draftKeyOf = (routeId: string, serviceDate: string): string => `${routeId}|${serviceDate}`

interface RouteProductivityDraftState {
  drafts: Record<string, ProductivityDraft>
}

const initialState: RouteProductivityDraftState = {
  drafts: {},
}

const routeProductivityDraftSlice = createSlice({
  name: ROUTE_PRODUCTIVITY_DRAFT_KEY,
  initialState,
  reducers: {
    upsertDraft: (state, action: PayloadAction<{ key: string; patch: ProductivityDraft }>) => {
      const { key, patch } = action.payload
      state.drafts[key] = { ...state.drafts[key], ...patch }
    },
    clearDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload]
    },
    /**
     * Drops only the fields a save actually persisted. Anything the endpoint has
     * no field for stays pending, so the bar keeps reporting it instead of the
     * save quietly discarding an edit nobody stored.
     */
    clearDraftFields: (state, action: PayloadAction<{ key: string; fields: ReadonlyArray<keyof ProductivityDraft> }>) => {
      const { key, fields } = action.payload
      const draft = state.drafts[key]
      if (!draft) return
      fields.forEach(field => {
        delete draft[field]
      })
      if (Object.keys(draft).length === 0) delete state.drafts[key]
    },
    // A retry still reading `id: null` would create the ticket a second time and orphan the first.
    setDraftTicketId: (state, action: PayloadAction<{ key: string; index: number; id: string }>) => {
      const { key, index, id } = action.payload
      const ticket = state.drafts[key]?.disposalTickets?.[index]
      if (ticket) ticket.id = id
    },
    clearAllDrafts: state => {
      state.drafts = {}
    },
  },
})

export const { upsertDraft, clearDraft, clearDraftFields, setDraftTicketId, clearAllDrafts } = routeProductivityDraftSlice.actions

export interface RouteProductivityDraftRootStateSlice {
  [ROUTE_PRODUCTIVITY_DRAFT_KEY]?: RouteProductivityDraftState
}

export const selectProductivityDrafts = (state: RouteProductivityDraftRootStateSlice): Record<string, ProductivityDraft> =>
  state[ROUTE_PRODUCTIVITY_DRAFT_KEY]?.drafts ?? initialState.drafts

export default routeProductivityDraftSlice.reducer
